/**
 * Las reglas del wizard de compras, sin React.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Aplicar una compra suma stock y reescribe el costo de reposicion de cada
 * producto tocado —y con eso su precio de venta—. Lo que decide si eso esta
 * bien no es el JSX: es (1) que el total de las lineas cierre contra el total
 * del comprobante, (2) que el wizard pregunte por lo que no sabe y no moleste
 * con lo que si, y (3) que el resumen de "que va a pasar" sea el numero
 * verdadero. Todo eso vive aca y se prueba con filas sueltas.
 *
 * LA FORMA DE LOS DATOS
 * ---------------------
 * Espejo de `supabase/migrations/0029_compras.sql` y de los Zod de
 * `packages/backend/src/repos/compras.ts`. Los `numeric` se tipan `Numerico`
 * igual que en el resto del frontend.
 */
import {
  aplicarRedondeo,
  calcularPrecio,
  convertir,
  despejarPrecio,
  formatear,
  formatearNumero,
  parsearImporte,
  redondear,
  type MarkupTipo,
  type Moneda,
  type Redondeo,
} from "@tora/shared";

import { aNumero, type Numerico } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

export {
  EXTENSIONES_ACEPTADAS,
  LIMITE_BYTES_ARCHIVO,
  formatearBytes,
  formatearDuracion,
  validarArchivo,
} from "@/components/listas/logica";

// ─────────────────────────────────────────────────────────────────────────────
// Los datos, tal como los devuelve el backend
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoCompra =
  "PENDIENTE" | "PROCESANDO" | "COMPLETADO" | "ERROR" | "APLICADO" | "DESCARTADO";

/** Como se lee cada estado. Un solo texto para el historial y la ficha. */
export const ETIQUETA_ESTADO: Record<EstadoCompra, string> = {
  PENDIENTE: "En cola",
  PROCESANDO: "Leyendo",
  COMPLETADO: "Por revisar",
  ERROR: "No se pudo leer",
  APLICADO: "Aplicada",
  DESCARTADO: "Descartada",
};

export type TonoEstado = "warning" | "success" | "destructive";

/**
 * MASTER seccion 10: el remito en curso (en cola, leyendo, por revisar) es
 * `warning` porque requiere atencion pero no esta mal; aplicada `success`;
 * descartada y con error `destructive`.
 */
export const TONO_ESTADO: Record<EstadoCompra, TonoEstado> = {
  PENDIENTE: "warning",
  PROCESANDO: "warning",
  COMPLETADO: "warning",
  ERROR: "destructive",
  APLICADO: "success",
  DESCARTADO: "destructive",
};

export type TipoMatch =
  "CODIGO_EXTERNO" | "CODIGO_INTERNO" | "NOMBRE" | "MANUAL" | "NUEVO" | "SIN_MATCH";

export const ACCIONES_COMPRA = ["INGRESAR", "CREAR_E_INGRESAR", "IGNORAR"] as const;
export type AccionFilaCompra = (typeof ACCIONES_COMPRA)[number];

export const TIPOS_COMPROBANTE = [
  "FACTURA_A",
  "FACTURA_B",
  "FACTURA_C",
  "REMITO",
  "PRESUPUESTO",
  "OTRO",
] as const;
export type TipoComprobante = (typeof TIPOS_COMPROBANTE)[number];

export const TEXTO_COMPROBANTE: Record<TipoComprobante, string> = {
  FACTURA_A: "Factura A",
  FACTURA_B: "Factura B",
  FACTURA_C: "Factura C",
  REMITO: "Remito",
  PRESUPUESTO: "Presupuesto",
  OTRO: "Comprobante",
};

/**
 * Incluye todos los campos de `ImportLista` (listas/logica.ts) con los mismos
 * nombres: `ProcesoImport` se reutiliza tal cual mientras la IA lee.
 */
export interface Compra {
  id: string;
  proveedor_id: number | null;
  proveedor_detectado: string | null;
  cuit_detectado: string | null;
  /** Null en las dos: la compra se cargo a mano, sin remito. */
  archivo_nombre: string | null;
  archivo_bytes: Numerico | null;
  archivo_url: string | null;
  estado: EstadoCompra;
  tipo_comprobante: TipoComprobante | null;
  numero_comprobante: string | null;
  numero_remito: string | null;
  fecha_comprobante: string | null;
  moneda: Moneda | null;
  cotizacion: Numerico | null;
  total_declarado: Numerico | null;
  /** Lo que se le debe al proveedor. Null = resolver al aplicar. */
  total_a_pagar: Numerico | null;
  /**
   * El importe que se tildo en «Cuánto le debo» (0121_9c). Opcional: una base
   * sin la migracion no lo manda.
   */
  deuda_confirmada?: Numerico | null;
  /** Si esta compra asienta en la cuenta corriente del proveedor. */
  genera_deuda: boolean;
  moneda_detectada: "ARS" | "USD" | "MIXTA" | null;
  notas: string | null;
  error_codigo: string | null;
  error_mensaje: string | null;
  modelo: string | null;
  tokens_entrada: number | null;
  tokens_salida: number | null;
  duracion_ms: number | null;
  filas_total: number;
  lote_id: string | null;
  aplicado_at: string | null;
  /**
   * Las unidades de esta compra que cerraron un encargo (0111). Solo viene al
   * leer una compra suelta: el resumen de una compra aplicada las contaba
   * como stock.
   */
  encargos_recibidos?: number;
  /**
   * Solo en el historial: lo que suman las lineas que entran, en la moneda de
   * la compra. Es el importe que se muestra cuando no hay total cargado.
   */
  total_lineas?: Numerico | null;
  created_at: string;
  finished_at: string | null;
}

/** Una linea de `v_compra_revision`. */
export interface FilaCompra {
  id: number;
  compra_id: string;
  orden: number;
  codigo_externo: string | null;
  descripcion: string;
  cantidad: Numerico;
  costo_neto: Numerico;
  iva_pct: Numerico;
  moneda: Moneda;
  costo_final: Numerico;
  match_tipo: TipoMatch | null;
  match_confianza: Numerico | null;
  accion: AccionFilaCompra;
  decidida: boolean;
  aplicada: boolean;
  error: string | null;
  costo_anterior: Numerico | null;
  moneda_anterior: Moneda | null;
  variacion_pct: Numerico | null;
  cantidad_recibida: Numerico | null;
  motivo_diferencia: string | null;
  nuevo_nombre: string | null;
  nuevo_rubro_id: number | null;
  nuevo_codigo_interno: string | null;
  nuevo_precio_manual: Numerico | null;
  nuevo_markup_pct: Numerico | null;
  ingreso_id: number | null;
  producto_id: number | null;
  codigo_interno: string | null;
  producto_nombre: string | null;
  stock_actual: Numerico | null;
  precio_actual: Numerico | null;
  precio_moneda: Moneda | null;
  precio_manual: Numerico | null;
  precio_resultante: Numerico | null;
  codigo_sugerido: string | null;
  producto_rubro_id: number | null;
  /**
   * Ya aplicada: el precio de venta de antes y el de despues, como los anoto el
   * lote de la compra (0122). Con la compra aplicada `precio_actual` ya es el
   * nuevo, y comparar contra el decia «Cambian de precio 0».
   */
  precio_anterior_lote?: Numerico | null;
  precio_nuevo_lote?: Numerico | null;
  /**
   * El costo sobre el que se calcula el precio hoy (0148): el anterior si una
   * compra trajo la mercaderia mas barata. Con el de la factura dice «no baja».
   */
  costo_precio_actual?: Numerico | null;
  /** Si el producto vinculado esta activo: si no, «vuelve a la venta». */
  producto_activo?: boolean | null;
  /**
   * 0153: el precio de venta revisado en la pantalla (en la moneda del precio
   * del producto; ARS si es nuevo) y el margen y el redondeo que lo dan. Null
   * es «sin revisar»: al cargar queda la regla.
   */
  precio_elegido?: Numerico | null;
  precio_markup?: Numerico | null;
  precio_redondeo?: Redondeo | null;
  /** Sobre que costo y con que regla sale el sugerido, para despejar uno escrito. */
  costo_base_precio?: Numerico | null;
  costo_base_moneda?: Moneda | null;
  markup_tipo?: MarkupTipo | null;
  markup_valor?: Numerico | null;
  markup_moneda?: Moneda | null;
  redondeo?: Redondeo | null;
  /** Lo que entro hoy a mano de este producto, sin pasar por una compra. */
  ingresado_a_mano_hoy?: Numerico | null;
}

export interface ResumenMatchingCompra {
  filas: number;
  con_match: number;
  sin_match: number;
  por_codigo: number;
  por_nombre: number;
}

export interface ResultadoAplicarCompra {
  compra_id: string;
  lote_id: string;
  ingresados: number;
  creados: number;
  /** Todas las de la factura, incluidas las que cerraron un encargo. */
  unidades: number;
  /** Las que cerraron un encargo en vez de quedar en el stock (0111). */
  encargos_recibidos?: number;
  /** Las lineas que dejaron alguna unidad en el stock (sin las que fueron enteras a encargos). */
  productos_al_stock?: number;
}

export interface ResultadoRevertirCompra {
  compra_id: string;
  lote_id: string;
  ingresos_borrados: number;
  productos_restaurados: number;
  productos_creados_que_quedan: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// El costo con IVA y los totales
// ─────────────────────────────────────────────────────────────────────────────

/** El mismo calculo que la columna `costo_final` de la base. */
export function costoConIva(neto: number, ivaPct: number): number {
  return redondear(neto * (1 + ivaPct / 100));
}

/** La cantidad que entra: la que se conto si se corrigio, si no la del remito. */
export function cantidadEfectiva(fila: FilaCompra): number {
  return fila.cantidad_recibida === null ? aNumero(fila.cantidad) : aNumero(fila.cantidad_recibida);
}

/**
 * Lo que suman las lineas, con IVA, en la cantidad DEL REMITO.
 *
 * Se suma sin redondear cada unitario y se redondea al final: es como lo hace
 * el proveedor. Las unitarias redondeadas de la factura de Elit suman 77,76;
 * la factura dice 77,77, y tiene razon.
 */
export function totalLineas(filas: ReadonlyArray<FilaCompra>): number {
  const suma = filas.reduce(
    (acc, f) => acc + aNumero(f.cantidad) * aNumero(f.costo_neto) * (1 + aNumero(f.iva_pct) / 100),
    0,
  );
  return redondear(suma);
}

export function unidadesDelRemito(filas: ReadonlyArray<FilaCompra>): number {
  return filas.reduce((acc, f) => acc + aNumero(f.cantidad), 0);
}

export interface VerificacionTotal {
  declarado: number | null;
  calculado: number;
  diferencia: number;
  tolerancia: number;
  cierra: boolean;
}

/**
 * El chequeo mas barato contra un punto decimal corrido.
 *
 * La tolerancia es la del redondeo: un centavo por unidad, o un 0,1% del
 * total, lo que sea mayor. Una linea leida como 87,80 en vez de 8,78 se pasa
 * de eso por mucho. Sin total declarado no hay contra que comparar y se deja
 * seguir: el operador lo ve en el encabezado y lo carga si lo tiene.
 */
export function verificarTotal(
  filas: ReadonlyArray<FilaCompra>,
  totalDeclarado: Numerico | null,
): VerificacionTotal {
  const calculado = totalLineas(filas);
  if (totalDeclarado === null) {
    return { declarado: null, calculado, diferencia: 0, tolerancia: 0, cierra: true };
  }
  const declarado = aNumero(totalDeclarado);
  const tolerancia = Math.max(0.01 * unidadesDelRemito(filas), 0.001 * declarado, 0.01);
  const diferencia = redondear(calculado - declarado);
  return {
    declarado,
    calculado,
    diferencia,
    tolerancia,
    cierra: Math.abs(diferencia) <= tolerancia,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// La deuda con el proveedor
// ─────────────────────────────────────────────────────────────────────────────

export interface DeudaPropuesta {
  importe: number;
  moneda: Moneda | null;
  /** La suma de lo que entra al stock, para el aviso cuando no coincide. */
  suma: number;
  /**
   * Lo que dice el papel: todas las lineas con las cantidades del remito, o el
   * total declarado si es mayor. Contra esto se mide si la deuda es «grande».
   */
  papel: number;
  coincideConLineas: boolean;
}

/** Las lineas que van a entrar al stock. */
function ingresa(f: FilaCompra): boolean {
  return f.accion === "INGRESAR" || f.accion === "CREAR_E_INGRESAR";
}

/**
 * Cuanto se le va a deber al proveedor por esta compra.
 *
 * El orden es el mismo que aplica `aplicar_compra` en la base, y no es
 * arbitrario: lo que se corrigio a mano, si no el total del papel, si no la suma
 * de lo que entra al stock.
 *
 * SE LE DEBE LO QUE DICE LA FACTURA, no lo que entro al stock. Si se ignoraron
 * lineas —un flete, un item que no se cataloga— o llegaron 8 de 10, el total
 * declarado sigue siendo lo que el proveedor va a cobrar. `coincideConLineas`
 * alimenta el aviso: avisa, no bloquea, porque las dos razones de arriba son
 * legitimas y frenar la carga por eso obligaria a mentirle al sistema.
 */
export function deudaPropuesta(
  compra: Pick<Compra, "total_declarado" | "total_a_pagar" | "moneda">,
  filas: ReadonlyArray<FilaCompra>,
): DeudaPropuesta {
  const moneda = compra.moneda ?? filas.find(ingresa)?.moneda ?? null;

  const suma = redondear(
    filas
      .filter((f) => ingresa(f) && f.moneda === moneda)
      .reduce((t, f) => t + aNumero(f.costo_final) * aNumero(f.cantidad_recibida ?? f.cantidad), 0),
  );

  const aMano = compra.total_a_pagar === null ? null : aNumero(compra.total_a_pagar);
  const declarado = compra.total_declarado === null ? null : aNumero(compra.total_declarado);
  const importe = aMano ?? declarado ?? suma;

  // Lo que dice el papel: todas las lineas —las ignoradas tambien— con las
  // cantidades del remito, o el total declarado si es mayor. Es la vara de la
  // sospecha «grande»: con 8 de 10 recibidas, o con un item que no se
  // cataloga, la factura es mas que lo que entra al stock y esta bien.
  const lineasDelPapel = redondear(
    filas
      .filter((f) => f.moneda === moneda)
      .reduce((t, f) => t + aNumero(f.costo_final) * aNumero(f.cantidad), 0),
  );
  const papel = Math.max(lineasDelPapel, declarado ?? 0);

  return { importe, moneda, suma, papel, coincideConLineas: Math.abs(importe - suma) < 0.01 };
}

/**
 * Lo que muestra «Cuánto le debo» al abrir la revision: lo guardado a mano, si
 * hay. Antes arrancaba vacio y lo guardado volvia como texto gris de ejemplo;
 * con solo entrar y salir del campo se borraba, y el flete se perdia.
 */
export function textoDeudaGuardada(compra: Pick<Compra, "total_a_pagar">): string {
  return compra.total_a_pagar === null ? "" : formatearNumero(aNumero(compra.total_a_pagar), 2);
}

/**
 * Si el tilde de «Cuánto le debo» ya se dio para este importe en otra visita.
 * Antes el tilde vivia solo en la pantalla: al recargar volvia destildado y
 * «Aplicar la compra» quedaba trabado sobre un importe que ya se habia mirado.
 * Vale solo para el mismo importe (al centavo, como lo guarda la base): si
 * alguien lo cambia, hay que volver a mirarlo.
 */
export function deudaConfirmadaGuardada(
  compra: Pick<Compra, "deuda_confirmada">,
  importe: number,
): boolean {
  const guardada = compra.deuda_confirmada;
  if (guardada === null || guardada === undefined) return false;
  return Math.round(aNumero(guardada) * 100) === Math.round(importe * 100);
}

/**
 * Lo escrito en «Cuánto le debo», como numero. Null si esta vacio o no se
 * entiende: entonces vale el propuesto.
 */
export function importeEscrito(texto: string): number | null {
  if (texto.trim() === "") return null;
  const n = parsearImporte(texto);
  return n !== null && Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Cuando lo que se le debe no se parece a lo que entra al stock.
 *
 *   · "pesos": la compra es en dolares y el importe dividido la cotizacion da
 *     mas o menos las lineas. Es la factura en pesos tipeada en un campo en
 *     dolares: 1.163.684 contra US$ 741 quedaba como una deuda de
 *     US$ 1.163.684, con un aviso azul que decia «Es normal».
 *   · "dolares": lo mismo al reves, en una compra en pesos.
 *   · "grande": mas de un 20% arriba de las lineas. Un flete no suele pesar
 *     tanto; puede serlo, por eso se pide confirmar y no se frena.
 *
 * Por debajo de las lineas no se avisa: que falte algo por cobrar es lo que
 * pasa con una bonificacion, y no mueve la deuda para arriba.
 */
export type SospechaDeDeuda = "pesos" | "dolares" | "grande";

export function sospechaDeDeuda(
  importe: number,
  suma: number,
  moneda: Moneda | null,
  cotizacion: number,
  /** Lo que dice el papel (`DeudaPropuesta.papel`): la vara de «grande». */
  papel = 0,
): SospechaDeDeuda | null {
  if (suma <= 0 || importe <= 0) return null;
  const razon = importe / suma;
  const pareceCotizacion = (r: number): boolean =>
    cotizacion > 1 && r >= cotizacion * 0.7 && r <= cotizacion * 1.3;
  if (moneda === "USD" && pareceCotizacion(razon)) return "pesos";
  if (moneda === "ARS" && pareceCotizacion(1 / razon)) return "dolares";
  if (importe / Math.max(suma, papel) > 1.2) return "grande";
  return null;
}

/**
 * Las lineas que entran al stock en otra moneda que la de la compra.
 *
 * La deuda propuesta suma solo las lineas en la moneda de la compra (es lo que
 * hace `aplicar_compra`): una linea en pesos en una compra en dolares no suma
 * a lo que se le debe. Pasaba sin querer —el renglon tomaba la moneda del
 * ultimo costo del producto— y la deuda quedaba en cero sin que nada lo
 * dijera. Se avisa en la revision; no se convierte sola.
 */
export function lineasEnOtraMoneda(
  compra: Pick<Compra, "moneda">,
  filas: ReadonlyArray<FilaCompra>,
): FilaCompra[] {
  if (compra.moneda === null) return [];
  return filas.filter((f) => ingresa(f) && f.moneda !== compra.moneda);
}

/** Cuantas unidades del remito no llegaron (las contadas de menos). */
export function unidadesQueNoLlegaron(filas: ReadonlyArray<FilaCompra>): number {
  return redondear(
    filas
      .filter((f) => ingresa(f) && f.cantidad_recibida !== null)
      .reduce((t, f) => t + Math.max(aNumero(f.cantidad) - aNumero(f.cantidad_recibida), 0), 0),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Que linea pregunta y cual no
// ─────────────────────────────────────────────────────────────────────────────

export type ClaseLinea = "LISTA" | "DUDOSA" | "SIN_MATCH" | "DECIDIDA";

/**
 * LISTA: matcheo por codigo exacto y nadie la toco. Entra sin preguntar.
 * DUDOSA: matcheo por parecido de nombre. Hay que confirmar "¿es este?".
 * SIN_MATCH: no hay producto. Hay que crearlo, buscarlo o dejarla afuera.
 * DECIDIDA: una persona ya dijo que hacer (o ya se aplico).
 */
export function clasificarLinea(fila: FilaCompra): ClaseLinea {
  if (fila.aplicada || fila.decidida) return "DECIDIDA";
  if (
    fila.accion === "INGRESAR" &&
    fila.producto_id !== null &&
    (fila.match_tipo === "CODIGO_EXTERNO" || fila.match_tipo === "CODIGO_INTERNO")
  ) {
    return "LISTA";
  }
  if (fila.producto_id !== null) return "DUDOSA";
  return "SIN_MATCH";
}

/** Las que el wizard tiene que preguntar, en el orden del remito. */
export function pendientesDelWizard(filas: ReadonlyArray<FilaCompra>): FilaCompra[] {
  return [...filas]
    .filter((f) => {
      const clase = clasificarLinea(f);
      return clase === "DUDOSA" || clase === "SIN_MATCH";
    })
    .sort((a, b) => a.orden - b.orden);
}

/** Las que entran sin que nadie las haya tocado. */
export function listasSinPreguntar(filas: ReadonlyArray<FilaCompra>): FilaCompra[] {
  return [...filas].filter((f) => clasificarLinea(f) === "LISTA").sort((a, b) => a.orden - b.orden);
}

/** Las que una persona resolvio, entren o no. */
export function resueltas(filas: ReadonlyArray<FilaCompra>): FilaCompra[] {
  return [...filas]
    .filter((f) => clasificarLinea(f) === "DECIDIDA")
    .sort((a, b) => a.orden - b.orden);
}

/** Dos lineas del remito que van al mismo producto. Se avisa, no se suman solas. */
export function duplicadas(filas: ReadonlyArray<FilaCompra>): Map<number, FilaCompra[]> {
  const porProducto = new Map<number, FilaCompra[]>();
  for (const f of filas) {
    if (f.producto_id === null || f.accion === "IGNORAR") continue;
    const lista = porProducto.get(f.producto_id) ?? [];
    lista.push(f);
    porProducto.set(f.producto_id, lista);
  }
  for (const [id, lista] of porProducto) {
    if (lista.length < 2) porProducto.delete(id);
  }
  return porProducto;
}

// ─────────────────────────────────────────────────────────────────────────────
// El alta de un producto desde el remito
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El nombre como lo escribe el catalogo: mayusculas, sin tildes, sin espacios
 * dobles. "Mouse NX-7000 Inalámbrico Negro" -> "MOUSE NX-7000 INALAMBRICO NEGRO".
 */
export function nombreCatalogo(descripcion: string): string {
  return descripcion.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toUpperCase();
}

/**
 * El rubro que mas aparece entre las lineas que si matchearon: un remito de
 * Elit trae accesorios, y el mouse nuevo casi seguro tambien lo es.
 */
export function rubroSugerido(filas: ReadonlyArray<FilaCompra>): number | null {
  const conteo = new Map<number, number>();
  for (const f of filas) {
    if (f.producto_rubro_id === null) continue;
    conteo.set(f.producto_rubro_id, (conteo.get(f.producto_rubro_id) ?? 0) + 1);
  }
  let mejor: number | null = null;
  let max = 0;
  for (const [rubro, n] of conteo) {
    if (n > max) {
      max = n;
      mejor = rubro;
    }
  }
  return mejor;
}

export interface DatosAlta {
  nombre: string;
  rubroId: number | null;
  codigoInterno: string | null;
  precio: number | null;
  markup: number | null;
}

/** Lo mismo que frena el backend con 422, un paso antes. */
export function validarAlta(datos: DatosAlta): string[] {
  const errores: string[] = [];
  if (datos.nombre.trim() === "") errores.push("El producto necesita un nombre.");
  if (datos.rubroId === null) errores.push("Elegí el rubro.");
  if (datos.precio === null && datos.markup === null) {
    errores.push("Poné un precio de venta o un margen.");
  }
  if (datos.precio !== null && datos.precio < 0) errores.push("El precio no puede ser negativo.");
  if (datos.markup !== null && datos.markup < 0) errores.push("El margen no puede ser negativo.");
  return errores;
}

// ─────────────────────────────────────────────────────────────────────────────
// Que va a pasar si aprieto Aplicar
// ─────────────────────────────────────────────────────────────────────────────

export interface CambioDePrecio {
  fila: FilaCompra;
  de: number;
  a: number;
  /**
   * Cuanto se mueve, como fraccion (-0,998 = baja un 99,8%). Sin esto la lista
   * mostraba $ 2.800 tachado y $ 25 al lado, y un precio mal tipeado en una
   * compra a mano se aplicaba sin que nada lo marcara.
   */
  variacion: number;
}

/** Una variacion de precio que se aplica recien despues de confirmarla. */
export const SALTO_DE_PRECIO = 0.5;

export function hayPreciosQueSaltan(cambios: ReadonlyArray<CambioDePrecio>): boolean {
  return cambios.some((c) => Math.abs(c.variacion) > SALTO_DE_PRECIO);
}

export interface ResumenAplicar {
  /** Productos distintos que reciben stock. */
  productos: number;
  /** Unidades que entran (las recibidas, no las del remito). */
  unidades: number;
  /** Productos que se dan de alta. */
  nuevos: number;
  /** Lo que vale lo que entra, a costo con IVA, por moneda. Nunca sumado. */
  porMoneda: Array<{ moneda: Moneda; total: number; lineas: number }>;
  /** Productos existentes cuyo precio de venta cambia al aplicar. */
  cambianPrecio: CambioDePrecio[];
  /** Lineas que el wizard todavia no resolvio. */
  sinDecidir: number;
  /** Lineas que quedan afuera por decision del operador. */
  afuera: number;
  total: number;
}

export function resumenAplicar(filas: ReadonlyArray<FilaCompra>): ResumenAplicar {
  const productos = new Set<string>();
  const porMoneda = new Map<Moneda, { moneda: Moneda; total: number; lineas: number }>();
  const cambianPrecio: CambioDePrecio[] = [];
  let unidades = 0;
  let nuevos = 0;
  let sinDecidir = 0;
  let afuera = 0;

  for (const f of filas) {
    const clase = clasificarLinea(f);
    if (clase === "DUDOSA" || clase === "SIN_MATCH") {
      sinDecidir += 1;
      continue;
    }
    if (f.accion === "IGNORAR") {
      afuera += 1;
      continue;
    }

    const cantidad = cantidadEfectiva(f);
    unidades += cantidad;

    // Ya aplicada, la fila que dio de alta el producto tiene su producto_id, y
    // el resumen de lo que entro decia «Se crean 0».
    if (f.accion === "CREAR_E_INGRESAR" && (f.producto_id === null || f.aplicada)) {
      nuevos += 1;
      productos.add(`nuevo-${f.id}`);
    } else if (f.producto_id !== null) {
      productos.add(`p-${f.producto_id}`);
    }

    const acumulado = porMoneda.get(f.moneda) ?? { moneda: f.moneda, total: 0, lineas: 0 };
    acumulado.total = redondear(acumulado.total + cantidad * aNumero(f.costo_final));
    acumulado.lineas += 1;
    porMoneda.set(f.moneda, acumulado);

    // Ya aplicada, el producto tiene el precio nuevo y compararlo contra si
    // mismo daba cero: el de antes y el de despues salen del lote (0122).
    const de = f.aplicada ? (f.precio_anterior_lote ?? null) : f.precio_actual;
    const a = f.aplicada
      ? (f.precio_nuevo_lote ?? null)
      : (f.precio_elegido ?? f.precio_resultante);
    if (
      f.accion === "INGRESAR" &&
      f.producto_id !== null &&
      (f.precio_manual === null || (f.precio_elegido ?? null) !== null) &&
      de !== null &&
      a !== null &&
      aNumero(de) !== aNumero(a)
    ) {
      const antes = aNumero(de);
      const despues = aNumero(a);
      cambianPrecio.push({
        fila: f,
        de: antes,
        a: despues,
        variacion: antes > 0 ? (despues - antes) / antes : 0,
      });
    }
  }

  return {
    productos: productos.size,
    unidades,
    nuevos,
    porMoneda: [...porMoneda.values()].sort((a, b) =>
      a.moneda === b.moneda ? 0 : a.moneda === "ARS" ? -1 : 1,
    ),
    cambianPrecio,
    sinDecidir,
    afuera,
    total: filas.length,
  };
}

export interface PuedeAplicar {
  ok: boolean;
  motivo: string | null;
}

/**
 * El costo que va a quedar en el producto. Si el proveedor cobra en dolares y
 * la linea viene en pesos, son los pesos divididos por la cotizacion de la
 * factura: el markup va sobre los dolares que se pagaron (0101). Sin
 * cotizacion todavia no hay a cuanto convertir y se muestra lo del papel.
 */
export function costoDelProducto(
  fila: Pick<FilaCompra, "costo_final" | "moneda">,
  compra: Pick<Compra, "cotizacion">,
  monedaProveedor: Moneda | null,
): { costo: number; moneda: Moneda } {
  const costo = aNumero(fila.costo_final);
  const cotizacion = aNumero(compra.cotizacion);
  if (monedaProveedor === "USD" && fila.moneda === "ARS" && cotizacion > 0) {
    return { costo: redondear(costo / cotizacion, 2), moneda: "USD" };
  }
  return { costo, moneda: fila.moneda };
}

/**
 * Una compra con dolares en juego sin la cotizacion de su factura: proveedor
 * que cobra en dolares, factura en dolares o alguna linea en dolares. El dolar
 * es el que tomo el proveedor; no hay uno general que lo reemplace (0101).
 */
export function faltaCotizacion(
  compra: Pick<Compra, "cotizacion" | "moneda">,
  filas: ReadonlyArray<Pick<FilaCompra, "moneda" | "accion" | "aplicada">>,
  monedaProveedor: Moneda | null,
): boolean {
  if (aNumero(compra.cotizacion) > 0) return false;
  if (monedaProveedor === "USD" || compra.moneda === "USD") return true;
  return filas.some(
    (f) =>
      f.moneda === "USD" &&
      !f.aplicada &&
      (f.accion === "INGRESAR" || f.accion === "CREAR_E_INGRESAR"),
  );
}

export function sePuedeAplicar(
  compra: Compra | null,
  resumen: ResumenAplicar,
  verificacion: VerificacionTotal,
  /** A quien le falta el dolar: el proveedor, o "el proveedor" si no hay. */
  sinCotizacionDe: string | null = null,
  /**
   * No se pudo leer la lista de encargos. Aplicar igual metia como stock libre
   * la unidad que un cliente espera, y el encargo quedaba abierto sin aviso.
   */
  encargosSinLeer = false,
): PuedeAplicar {
  if (compra === null) return { ok: false, motivo: "Todavía no hay compra." };
  if (compra.estado === "APLICADO") return { ok: false, motivo: "Esta compra ya está aplicada." };
  if (compra.estado !== "COMPLETADO") {
    return { ok: false, motivo: "La compra todavía no está lista para aplicar." };
  }
  if (sinCotizacionDe !== null) {
    return {
      ok: false,
      motivo: `Falta la cotización de la factura: cargá en el encabezado el dólar que te tomó ${sinCotizacionDe}.`,
    };
  }
  if (!verificacion.cierra) {
    return {
      ok: false,
      motivo:
        "El total de los productos no coincide con el del comprobante. Corregí la línea que está mal; si la diferencia es un flete o algo que no entra al stock, borrá el total declarado en el encabezado y poné lo que le debés en «Cuánto le debo».",
    };
  }
  if (resumen.sinDecidir > 0) {
    return {
      ok: false,
      motivo:
        resumen.sinDecidir === 1
          ? "Falta resolver 1 producto."
          : `Faltan resolver ${resumen.sinDecidir} productos.`,
    };
  }
  if (resumen.productos === 0) {
    return { ok: false, motivo: "No hay ningún producto marcado para ingresar." };
  }
  if (encargosSinLeer) {
    return {
      ok: false,
      motivo:
        "No se pudo leer la lista de encargos, así que no se sabe si algo de esta factura lo espera un cliente. Recargá la página antes de aplicar.",
    };
  }
  return { ok: true, motivo: null };
}

/** "Factura A 00027-00410877 · remito 00037-00000514", como lo escribe la base. */
/** Sin archivo no hubo lectura: la compra se cargo a mano. */
export function esManual(compra: Compra): boolean {
  return compra.archivo_nombre === null;
}

/**
 * Como se llama la compra en el historial: el proveedor del catalogo si esta
 * asignado, si no lo que decia el documento, si no el archivo. Una compra a
 * mano sin proveedor no existe (el endpoint lo exige), pero por las dudas.
 */
export function tituloDe(compra: Compra, nombreProveedor: string | null): string {
  return nombreProveedor ?? compra.proveedor_detectado ?? compra.archivo_nombre ?? "Compra a mano";
}

/**
 * La referencia dentro de una frase: «esta compra» cuando no hay comprobante.
 * `referenciaDe` devuelve «Compra» a secas en las compras a mano, y los
 * dialogos decian «Aplicar Compra» y «Se borran los ingresos de Compra».
 */
export function referenciaEnFrase(compra: Compra, articulo: "la" | "esta"): string {
  const ref = referenciaDe(compra);
  return ref === "Compra" ? `${articulo} compra` : ref;
}

/**
 * El importe de una compra en el historial: el mismo orden que la deuda
 * (`deudaPropuesta` y `aplicar_compra`), lo corregido en «Cuánto le debo», si
 * no el total del papel, si no la suma de las lineas. Solo mostraba el total
 * declarado, y las compras a mano quedaban sin importe; y una factura en pesos
 * pasada a dolares en «Cuánto le debo» se listaba con el total del papel y no
 * con lo que quedo como deuda.
 */
export function importeDelHistorial(
  compra: Pick<Compra, "total_declarado" | "total_a_pagar" | "total_lineas">,
): number | null {
  const importe = compra.total_a_pagar ?? compra.total_declarado ?? compra.total_lineas ?? null;
  return importe === null ? null : aNumero(importe);
}

/**
 * La franja de arriba de una compra a mano. Lleva el dolar del proveedor: un
 * error de tipeo en la cotizacion cambia el costo en pesos y la deuda, y antes
 * solo se veia tocando «Corregir».
 */
export function partesDeLaFranja(compra: Compra, proveedor: string | null): string[] {
  return [
    proveedor ?? "Sin proveedor",
    compra.fecha_comprobante ? formatearFecha(compra.fecha_comprobante) : null,
    compra.moneda ?? null,
    compra.moneda === "USD" && compra.cotizacion !== null
      ? `dólar ${formatearNumero(aNumero(compra.cotizacion), 2)}`
      : null,
    compra.numero_comprobante ? referenciaDe(compra) : null,
    compra.total_declarado !== null && compra.moneda
      ? `total ${formatear(aNumero(compra.total_declarado), compra.moneda)}`
      : null,
  ].filter((p): p is string => p !== null);
}

export function referenciaDe(compra: Compra): string {
  const tipo = compra.tipo_comprobante ? TEXTO_COMPROBANTE[compra.tipo_comprobante] : "Compra";
  const partes = [tipo, compra.numero_comprobante ?? ""].filter((p) => p !== "");
  if (compra.numero_remito && compra.tipo_comprobante !== "REMITO") {
    partes.push(`· remito ${compra.numero_remito}`);
  }
  return partes.join(" ");
}

/**
 * El texto de la confirmacion. Se arma aca para que diga siempre lo mismo.
 *
 * `encargos` son las unidades tildadas para cerrar un encargo: esas no quedan
 * en el stock, van al cliente que las espera. Antes el texto contaba todas las
 * de la factura como stock («6 entran») cuando entraban 5 y 1 cerraba un
 * encargo.
 */
export function textoDeAplicar(resumen: ResumenAplicar, encargos = 0): string {
  const partes: string[] = [];
  const alStock = Math.max(resumen.unidades - encargos, 0);
  if (alStock > 0 || encargos === 0) {
    // Con encargos no se dice «de N productos»: N contaba tambien el producto
    // que va entero al cliente, y no se sabe cuales quedan en el stock.
    const deProductos =
      encargos > 0
        ? ""
        : ` de ${resumen.productos} ${resumen.productos === 1 ? "producto" : "productos"}`;
    partes.push(
      `${alStock} ${alStock === 1 ? "unidad entra" : "unidades entran"} al stock${deProductos}`,
    );
  }
  if (encargos > 0) {
    partes.push(`${encargos} ${encargos === 1 ? "cierra un encargo" : "cierran encargos"}`);
  }
  if (resumen.nuevos > 0) {
    partes.push(`${resumen.nuevos} ${resumen.nuevos === 1 ? "se da" : "se dan"} de alta`);
  }
  if (resumen.cambianPrecio.length > 0) {
    partes.push(
      `${resumen.cambianPrecio.length} ${resumen.cambianPrecio.length === 1 ? "cambia" : "cambian"} de precio de venta`,
    );
  }
  const texto = partes.join(", ") + ".";
  return resumen.afuera > 0
    ? `${texto} ${resumen.afuera === 1 ? "1 producto queda" : `${resumen.afuera} productos quedan`} afuera.`
    : texto;
}

/**
 * Lo que los pagos cargados no cubren de la deuda, en la moneda de la deuda.
 * Es el «Falta cubrir» del bloque de deuda, y el dialogo de Aplicar lo repite:
 * con un pago parcial decia «con el pago que cargaste» y callaba lo que la base
 * si anota como deuda.
 */
export function faltaPorCubrir(
  importe: number,
  pagos: ReadonlyArray<{ cuenta_id: number; importe: number | null }>,
  cuentas: ReadonlyArray<{ id: number; moneda: Moneda }>,
  moneda: Moneda,
  cotizacion: number,
): number {
  const cubierto = pagos.reduce((t, p) => {
    const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
    return p.importe === null || !cuenta
      ? t
      : t + convertir(p.importe, cuenta.moneda, moneda, cotizacion);
  }, 0);
  return Math.round((importe - cubierto) * 100) / 100;
}

// ─────────────────────────────────────────────────────────────────────────────
// La compra en una sola pantalla
// ─────────────────────────────────────────────────────────────────────────────
//
// Lo que el sistema reconoce no se pregunta. Lo que se parece a algo se
// pregunta de a una («¿Es tu X?»). Lo que no se parece a nada se propone como
// producto nuevo. Cada renglon lleva una marca que habla del precio, y con una
// factura larga solo queda a la vista lo que cambia. Salio de las pruebas con
// usuarios del 29/10/2026 (variante D del prototipo).

/** Una sola marca por renglon. «Igual» y «se mantiene» se leian igual. */
export type MarcaLinea =
  "NUEVO" | "VUELVE" | "SUBE" | "BAJA" | "NO_BAJA" | "SIN_CAMBIOS" | "PRECIO_A_MANO" | "NO_ENTRA";

export const TEXTO_MARCA: Record<MarcaLinea, string> = {
  NUEVO: "nuevo",
  VUELVE: "vuelve a la venta",
  SUBE: "sube",
  BAJA: "baja",
  NO_BAJA: "no baja",
  SIN_CAMBIOS: "sin cambios",
  PRECIO_A_MANO: "precio a mano",
  NO_ENTRA: "no entra",
};

/** La linea que se pregunta: se parece a un producto, pero no es seguro. */
export function esPregunta(fila: FilaCompra): boolean {
  return clasificarLinea(fila) === "DUDOSA";
}

/** La linea sin producto que nadie decidio: se propone como nueva. */
export function esPropuestaNueva(fila: FilaCompra): boolean {
  return clasificarLinea(fila) === "SIN_MATCH";
}

/** Se crea al aplicar: decidida como alta, o propuesta. */
function seCrea(fila: FilaCompra): boolean {
  return (
    (fila.accion === "CREAR_E_INGRESAR" && fila.producto_id === null) || esPropuestaNueva(fila)
  );
}

export function marcaDeLinea(
  fila: FilaCompra,
  compra: Pick<Compra, "cotizacion">,
  monedaProveedor: Moneda | null,
): MarcaLinea {
  if (fila.accion === "IGNORAR" && !esPropuestaNueva(fila)) return "NO_ENTRA";
  if (seCrea(fila)) return "NUEVO";
  if (fila.producto_activo === false) return "VUELVE";
  if (fila.precio_manual !== null) return "PRECIO_A_MANO";
  const antes = fila.precio_actual === null ? null : aNumero(fila.precio_actual);
  const despues = fila.precio_resultante === null ? null : aNumero(fila.precio_resultante);
  if (antes !== null && despues !== null && despues > antes) return "SUBE";
  const costo = costoDelProducto(fila, compra, monedaProveedor);
  const fijado = fila.costo_precio_actual == null ? null : aNumero(fila.costo_precio_actual);
  if (fijado !== null && costo.moneda === fila.moneda_anterior && costo.costo < fijado) {
    return "NO_BAJA";
  }
  return "SIN_CAMBIOS";
}

/** Lo que conviene mirar antes de cargar: lo que cambia de verdad. */
export function esParaMirar(marca: MarcaLinea): boolean {
  return (
    marca === "NUEVO" ||
    marca === "VUELVE" ||
    marca === "SUBE" ||
    marca === "BAJA" ||
    marca === "NO_ENTRA"
  );
}

/** Hasta cuantas lineas se ven todas. Con mas, lo que no cambia se pliega. */
export const LINEAS_SIN_PLEGAR = 6;

/**
 * El alta que se propone para una linea sin producto: el nombre como lo
 * escribe el catalogo, el rubro de las otras lineas y su margen habitual. Sin
 * margen conocido el precio queda por poner (`markup` null).
 */
export function altaPropuesta(
  fila: FilaCompra,
  rubroId: number | null,
  margenes: ReadonlyMap<number, number>,
): DatosAlta {
  return {
    nombre: fila.nuevo_nombre ?? nombreCatalogo(fila.descripcion),
    rubroId: fila.nuevo_rubro_id ?? rubroId,
    codigoInterno: fila.nuevo_codigo_interno,
    precio: fila.nuevo_precio_manual === null ? null : aNumero(fila.nuevo_precio_manual),
    markup:
      fila.nuevo_markup_pct !== null
        ? aNumero(fila.nuevo_markup_pct)
        : fila.nuevo_precio_manual === null && rubroId !== null
          ? (margenes.get(fila.nuevo_rubro_id ?? rubroId) ?? null)
          : null,
  };
}

/** Lo que se le manda a la linea para dejarla como alta. */
export function cambiosDeAlta(alta: DatosAlta): {
  accion: "CREAR_E_INGRESAR";
  producto_id: null;
  nuevo_nombre: string;
  nuevo_rubro_id: number | null;
  nuevo_codigo_interno: string | null;
  nuevo_precio_manual: number | null;
  nuevo_markup_pct: number | null;
} {
  return {
    accion: "CREAR_E_INGRESAR",
    producto_id: null,
    nuevo_nombre: alta.nombre.trim(),
    nuevo_rubro_id: alta.rubroId,
    nuevo_codigo_interno: alta.codigoInterno,
    nuevo_precio_manual: alta.precio,
    nuevo_markup_pct: alta.markup,
  };
}

/** Las formas de pago de un toque. Las demas van por «Otra forma». */
export type FormaDePago = "DEBO" | "EFECTIVO" | "BANCO" | "MERCADO_PAGO";

const TIPO_DE_CUENTA: Record<Exclude<FormaDePago, "DEBO">, string> = {
  EFECTIVO: "CAJA",
  BANCO: "BANCO",
  MERCADO_PAGO: "BILLETERA",
};

/**
 * La cuenta para pagar todo de un toque: la del tipo en la moneda de la deuda
 * y, si no hay, la de pesos. Null si el local no tiene una de ese tipo.
 */
export function cuentaParaPagar<
  C extends { id: number; tipo: string; moneda: Moneda; activo?: boolean },
>(forma: Exclude<FormaDePago, "DEBO">, cuentas: ReadonlyArray<C>, moneda: Moneda): C | null {
  const tipo = TIPO_DE_CUENTA[forma];
  const activas = cuentas.filter((c) => c.tipo === tipo && c.activo !== false);
  return (
    activas.find((c) => c.moneda === moneda) ?? activas.find((c) => c.moneda === "ARS") ?? null
  );
}

/** Que dice el boton y la linea de la deuda segun la forma elegida. */
export function textoDeLaForma(forma: FormaDePago | "OTRA", proveedor: string): string {
  switch (forma) {
    case "DEBO":
      return `a cuenta de ${proveedor}`;
    case "EFECTIVO":
      return "pagada en efectivo";
    case "BANCO":
      return "pagada por banco";
    case "MERCADO_PAGO":
      return "pagada con Mercado Pago";
    default:
      return "con el pago que cargaste";
  }
}

const RELLENO = new Set(["DE", "DEL", "LA", "EL", "CON", "PARA", "Y", "X", "P/", "C/"]);

/**
 * Con que arranca el buscador cuando la linea es otro producto: las dos
 * primeras palabras que dicen algo. El texto entero del proveedor no
 * encontraba nada («SILICONE CASE MAGSAFE / CASE MAGSAFE METAL GLITTER»).
 */
export function palabrasDeBusqueda(descripcion: string): string {
  // «ignite 40k» no encontraba nada: el producto es «VAPE V400 - 40000 PUFFS».
  // Con un numero de miles en el remito, se busca ese numero solo (0153).
  const miles = /\b(\d+(?:[.,]\d+)?)\s*k\b/i.exec(descripcion);
  if (miles) return expandirMiles(miles[0]);
  return nombreCatalogo(descripcion)
    .split(/[\s/(),.-]+/)
    .filter((p) => p.length >= 3 && !RELLENO.has(p) && !/^\d+$/.test(p))
    .slice(0, 2)
    .join(" ")
    .toLowerCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// El precio de venta se revisa en la compra (0153)
// ─────────────────────────────────────────────────────────────────────────────
// PEDIDO DEL LOCAL, 04/11/2026: «me tendria que mostrar una sugerencia de
// precio de venta, y si me parece bien o si lo quiero cambiar». El remito de
// Foxbox entro con el margen de siempre y al otro dia hubo que corregir todos
// los precios a mano. Ahora cada renglon muestra el sugerido, se puede
// escribir otro, y no se carga hasta revisarlos todos.

/** Lo que dice el renglon del precio de venta. */
export interface PrecioDelRenglon {
  moneda: Moneda;
  /** Con la regla: el margen de siempre sobre el costo de esta compra. */
  sugerido: number | null;
  /** El que tiene hoy el producto. Null si es nuevo. */
  hoy: number | null;
  /** Con lo que arranca el campo: el sugerido, o el de hoy si el sugerido es mas bajo. */
  inicial: number | null;
}

/**
 * El precio de venta de un renglon.
 *
 * Un producto existente sugiere lo que calcula la base (`precio_resultante`:
 * la regla con el costo nuevo, que no baja si vino mas barato). Uno nuevo, el
 * margen del rubro sobre el costo, terminado en 990 como el resto del local
 * cuando pasa de mil pesos.
 *
 * El campo arranca con el sugerido, salvo que sea mas bajo que el de hoy: un
 * precio no baja solo (decision del 05/11/2026).
 */
export function precioDelRenglon(
  fila: FilaCompra,
  marca: MarcaLinea,
  alta: DatosAlta | null,
  costo: { costo: number; moneda: Moneda },
  cotizacion: number,
): PrecioDelRenglon | null {
  if (marca === "NO_ENTRA") return null;
  if (marca === "NUEVO") {
    const precioAlta =
      alta?.precio ?? (fila.nuevo_precio_manual == null ? null : aNumero(fila.nuevo_precio_manual));
    const markup =
      alta?.markup ?? (fila.nuevo_markup_pct == null ? null : aNumero(fila.nuevo_markup_pct));
    let sugerido: number | null = precioAlta;
    if (sugerido === null && markup !== null && costo.costo > 0) {
      const bruto = calcularPrecio({
        costo: costo.costo,
        costoMoneda: costo.moneda,
        markupTipo: "PORCENTAJE",
        markupValor: markup,
        markupMoneda: costo.moneda,
        precioMoneda: "ARS",
        redondeo: "NINGUNO",
        precioManual: null,
        cotizacion,
      }).precioVenta;
      sugerido = aplicarRedondeo(bruto, bruto >= 1000 ? "TERMINACION_990" : "UNIDAD");
    }
    return { moneda: "ARS", sugerido, hoy: null, inicial: sugerido };
  }
  const moneda = fila.precio_moneda ?? "ARS";
  const hoy = fila.precio_actual == null ? null : aNumero(fila.precio_actual);
  const sugerido = fila.precio_resultante == null ? hoy : aNumero(fila.precio_resultante);
  const inicial = sugerido !== null && hoy !== null && hoy > sugerido ? hoy : sugerido;
  return { moneda, sugerido, hoy, inicial };
}

/** El precio que se ve en el campo: lo que se escribio, lo revisado, o el inicial. */
export function precioEnElCampo(
  fila: FilaCompra,
  precio: PrecioDelRenglon | null,
  borrador: number | null | undefined,
): number | null {
  if (borrador !== undefined) return borrador;
  if (fila.precio_elegido != null) return aNumero(fila.precio_elegido);
  return precio?.inicial ?? null;
}

/** Revisado: se guardo un precio y no se escribio otro encima. */
export function precioRevisado(fila: FilaCompra, borrador: number | null | undefined): boolean {
  if (fila.precio_elegido == null) return false;
  return borrador === undefined || borrador === aNumero(fila.precio_elegido);
}

/** El margen que deja un precio sobre el costo de la compra, en %. */
export function margenDelPrecio(
  precio: number,
  costo: { costo: number; moneda: Moneda },
  monedaPrecio: Moneda,
  cotizacion: number,
): number | null {
  if (costo.costo <= 0 || precio <= 0) return null;
  const costoEnPrecio =
    costo.moneda === monedaPrecio
      ? costo.costo
      : cotizacion > 0
        ? convertir(costo.costo, costo.moneda, monedaPrecio, cotizacion)
        : null;
  if (costoEnPrecio === null || costoEnPrecio <= 0) return null;
  return (precio / costoEnPrecio - 1) * 100;
}

/** Lo que se le manda a la linea al revisar el precio. */
export interface CambiosPrecio {
  precio_elegido: number;
  precio_markup: number | null;
  precio_redondeo: Redondeo | null;
}

/**
 * El precio revisado con el margen que lo da, despejado con la misma cuenta
 * que la ficha (`despejarPrecio`): al cargar cambia el margen y el precio
 * sigue al costo de ahi en adelante. Un producto con el precio escrito, o sin
 * costo para despejar, lo deja escrito (`precio_markup` null).
 */
export function cambiosDePrecio(
  fila: FilaCompra,
  marca: MarcaLinea,
  precio: number,
  costo: { costo: number; moneda: Moneda },
  cotizacion: number,
): CambiosPrecio {
  const escrito: CambiosPrecio = {
    precio_elegido: precio,
    precio_markup: null,
    precio_redondeo: null,
  };
  if (marca === "NUEVO") {
    if (costo.costo <= 0) return escrito;
    const d = despejarPrecio({
      costo: costo.costo,
      costoMoneda: costo.moneda,
      precioDeseado: precio,
      precioMoneda: "ARS",
      markupTipo: "PORCENTAJE",
      markupMoneda: costo.moneda,
      redondeo: "NINGUNO",
      cotizacion,
    });
    return d
      ? { precio_elegido: precio, precio_markup: d.markupValor, precio_redondeo: d.redondeo }
      : escrito;
  }
  if (fila.precio_manual !== null) return escrito;
  const base = fila.costo_base_precio == null ? null : aNumero(fila.costo_base_precio);
  if (base === null || base <= 0 || !fila.costo_base_moneda || !fila.precio_moneda) return escrito;
  const d = despejarPrecio({
    costo: base,
    costoMoneda: fila.costo_base_moneda,
    precioDeseado: precio,
    precioMoneda: fila.precio_moneda,
    markupTipo: fila.markup_tipo ?? "PORCENTAJE",
    ...(fila.markup_moneda ? { markupMoneda: fila.markup_moneda } : {}),
    redondeo: fila.redondeo ?? "NINGUNO",
    cotizacion,
  });
  return d
    ? { precio_elegido: precio, precio_markup: d.markupValor, precio_redondeo: d.redondeo }
    : escrito;
}

/**
 * La marca con el precio que se va a cargar: si se escribio otro que el de
 * hoy, sube o baja aunque la regla dijera «sin cambios».
 */
export function marcaConPrecio(
  marca: MarcaLinea,
  hoy: number | null,
  campo: number | null,
): MarcaLinea {
  if (marca === "NUEVO" || marca === "VUELVE" || marca === "NO_ENTRA") return marca;
  if (hoy === null || campo === null || campo === hoy) {
    return marca === "SUBE" ? "SIN_CAMBIOS" : marca;
  }
  return campo > hoy ? "SUBE" : "BAJA";
}

/** «ignite 40k» busca «40000»: los vapers se llaman por los puffs, no por la marca. */
export function expandirMiles(texto: string): string {
  return texto.replace(/\b(\d+(?:,\d+)?)\s*k\b/gi, (entero, n: string) => {
    const miles = parsearImporte(n);
    return miles === null ? entero : String(Math.round(miles * 1000));
  });
}
